import { ArrowLeft, CalendarClock, Mail } from 'lucide-react';
import { Button } from '../ui/button';
import { PathkitLogo, PK_COLORS } from '../PathkitLogo';

const SALES_EMAIL = 'sales@pathkit.pro';

export function BookDemo() {
  return (
    <div className="min-h-screen bg-white">
      <nav className="border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            <a href="/" className="flex items-center gap-2">
              <PathkitLogo className="w-8 h-8" />
              <span className="text-lg font-semibold" style={{ color: PK_COLORS.navy }}>
                PathKit
              </span>
            </a>
            <Button variant="ghost" size="sm" asChild>
              <a href="/" className="inline-flex items-center gap-2">
                <ArrowLeft className="w-4 h-4" /> Back to home
              </a>
            </Button>
          </div>
        </div>
      </nav>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <div
          className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs text-slate-600"
        >
          <CalendarClock className="w-3.5 h-3.5" style={{ color: PK_COLORS.teal }} />
          30 minutes, no slides
        </div>

        <h1
          className="mt-6 text-4xl md:text-5xl font-semibold tracking-tight"
          style={{ color: PK_COLORS.navy }}
        >
          See PathKit in your context
        </h1>
        <p className="mt-4 text-lg text-slate-600">
          We&rsquo;ll walk through scope intake, the Discovery Grid, and pricing for your firm size.
          Bring an example SOW and we&rsquo;ll analyze it live.
        </p>

        <div
          className="mt-10 rounded-xl border border-slate-200 p-8 text-center"
          style={{ backgroundColor: '#F8FAFC' }}
        >
          <p className="text-sm text-slate-600">
            Live calendar booking is coming soon. For now, email us and we&rsquo;ll get back the
            same business day.
          </p>
          <Button
            asChild
            size="lg"
            className="mt-5 hover:opacity-90"
            style={{ backgroundColor: PK_COLORS.navy, color: '#ffffff' }}
          >
            <a
              href={`mailto:${SALES_EMAIL}?subject=PathKit%20demo%20request&body=Hi%20PathKit%20team%2C%0A%0AI%27d%20like%20to%20see%20a%20demo.%20Best%20times%20for%20me%3A%0A%0AFirm%20name%3A%0AService%20desk%20platforms%20we%20support%3A%0ATeam%20size%3A`}
              className="inline-flex items-center gap-2"
              style={{ color: '#ffffff' }}
            >
              <Mail className="w-4 h-4" />
              Email {SALES_EMAIL}
            </a>
          </Button>
          <p className="mt-3 text-xs text-slate-500">
            Already a customer?{' '}
            <a href="/login" className="underline hover:text-slate-700">
              Sign in
            </a>
            .
          </p>
        </div>

        <div className="mt-12 grid gap-3 sm:grid-cols-3">
          <Highlight title="Scope intake demo" body="Paste an SOW and watch PathKit pull out config items and risks." />
          <Highlight title="Discovery walkthrough" body="See how guided questions and AI follow-ups close the gaps." />
          <Highlight title="Multi-tenant tour" body="Understand how isolation works across firms, clients, and engagements." />
        </div>
      </main>
    </div>
  );
}

function Highlight({ title, body }: { title: string; body: string }) {
  return (
    <div className="rounded-lg border border-slate-200 p-4">
      <div className="text-sm font-semibold" style={{ color: PK_COLORS.navy }}>
        {title}
      </div>
      <p className="mt-1 text-xs text-slate-600 leading-relaxed">{body}</p>
    </div>
  );
}
